/**
 * Staffing Status Report Page
 *
 * Shows event staffing gaps and fill rates.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getStaffingStatus, downloadReportCSV, StaffingStatusData } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, useReportDateRange } from '../../../components/reports';
import { formatInTimeZone } from '../../../utils/dateFormatting';

export default function StaffingStatusPage() {
  const [dateRange, setDateRange] = useReportDateRange();
  const [report, setReport] = useState<StaffingStatusData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getStaffingStatus({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setReport(response.report);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadReportCSV(
        '/reports/staffing-status',
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        'staffing-status.csv'
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatInTimeZone(dateString, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div>
      <nav className="mb-4">
        <Link to="/admin/reports" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Reports
        </Link>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Staffing Status Report</h1>
        <p className="text-gray-600 mt-1">Event staffing gaps and fill rates</p>
      </div>

      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : report.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">No staffing data for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Needed</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Filled</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Confirmed</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fill Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Confirm Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.map((row) => (
                  <tr key={row.eventId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link to={`/admin/events/${row.eventId}`} className="font-medium text-gray-900 hover:text-primary-600">
                        {row.eventName}
                      </Link>
                      <p className="text-sm text-gray-500">{row.venue}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(row.eventDate)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.rolesNeeded}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.rolesFilled}</td>
                    <td className="px-6 py-4 text-right text-sm text-green-600">{row.rolesConfirmed}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        row.fillRate >= 100 ? 'bg-green-100 text-green-800' :
                        row.fillRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {row.fillRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        row.confirmRate >= 80 ? 'bg-green-100 text-green-800' :
                        row.confirmRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {row.confirmRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
